// api.js - Secure API client

import { getToken, getuserId } from "./auth";
import { nowIso } from "./appDateTime";

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Adobe Sign API Configuration
const ADOBE_SIGN_CONFIG = {
  baseUrl:
    process.env.REACT_APP_ADOBE_SIGN_BASE_URL ||
    "https://api.na3.adobesign.com/api/rest/v6",
  clientId: process.env.REACT_APP_ADOBE_SIGN_CLIENT_ID,
  clientSecret: process.env.REACT_APP_ADOBE_SIGN_CLIENT_SECRET,
  redirectUri:
    process.env.REACT_APP_ADOBE_SIGN_REDIRECT_URI ||
    `${window.location.origin}/adobe-sign-callback`,
  scope:
    "user_read user_write agreement_read agreement_write agreement_send library_read library_write workflow_read workflow_write webhook_read webhook_write application_read application_write",
  authUrl: "https://secure.na3.adobesign.com/public/oauth/v2",
  tokenUrl: "https://api.na3.adobesign.com/oauth/v2/token",
};

// Adobe Sign OAuth 2.0 Token Management
class AdobeSignAuth {
  constructor() {
    this.accessToken = null;
    this.refreshToken = null;
    this.tokenExpiry = null;
    this.codeVerifier = null;
    this.loadStoredTokens();
  }

  // Load tokens from localStorage
  loadStoredTokens() {
    try {
      const stored = localStorage.getItem("adobe_sign_tokens");
      if (stored) {
        const tokens = JSON.parse(stored);
        this.accessToken = tokens.accessToken;
        this.refreshToken = tokens.refreshToken;
        this.tokenExpiry = tokens.tokenExpiry;
      }

      // Load code verifier if available
      const codeVerifier = localStorage.getItem("adobe_sign_code_verifier");
      if (codeVerifier) {
        this.codeVerifier = codeVerifier;
      }
    } catch (error) {
      console.error("Error loading stored Adobe Sign tokens:", error);
      this.clearTokens();
    }
  }

  // Save tokens to localStorage
  saveTokens(accessToken, refreshToken, expiresIn) {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    this.tokenExpiry = Date.now() + expiresIn * 1000;

    localStorage.setItem(
      "adobe_sign_tokens",
      JSON.stringify({
        accessToken: this.accessToken,
        refreshToken: this.refreshToken,
        tokenExpiry: this.tokenExpiry,
      })
    );
  }

  // Clear stored tokens
  clearTokens() {
    this.accessToken = null;
    this.refreshToken = null;
    this.tokenExpiry = null;
    this.codeVerifier = null;
    localStorage.removeItem("adobe_sign_tokens");
    localStorage.removeItem("adobe_sign_code_verifier");
  }

  // Check if token is valid and not expired
  isTokenValid() {
    return (
      this.accessToken && this.tokenExpiry && Date.now() < this.tokenExpiry
    );
  }

  // Get valid access token (refresh if needed)
  async getValidAccessToken() {
    if (this.isTokenValid()) {
      return this.accessToken;
    }

    if (this.refreshToken) {
      try {
        await this.refreshAccessToken();
        return this.accessToken;
      } catch (error) {
        console.error("Error refreshing Adobe Sign token:", error);
        this.clearTokens();
        throw new Error(
          "Failed to refresh Adobe Sign token. Please re-authenticate."
        );
      }
    }

    throw new Error(
      "No valid Adobe Sign token available. Please authenticate."
    );
  }

  // Refresh access token using refresh token
  async refreshAccessToken() {
    if (!this.refreshToken) {
      throw new Error("No refresh token available");
    }

    const response = await fetch(ADOBE_SIGN_CONFIG.tokenUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: new URLSearchParams({
        grant_type: "refresh_token",
        refresh_token: this.refreshToken,
        client_id: ADOBE_SIGN_CONFIG.clientId,
        client_secret: ADOBE_SIGN_CONFIG.clientSecret,
      }),
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Token refresh failed: ${error}`);
    }

    const data = await response.json();
    this.saveTokens(data.access_token, data.refresh_token, data.expires_in);
  }

  // Generate OAuth 2.0 authorization URL with PKCE
  async getAuthorizationUrl() {
    // Generate PKCE parameters
    const codeVerifier = this.generateCodeVerifier();
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);

    // Store code verifier for later use (both in memory and localStorage)
    this.codeVerifier = codeVerifier;
    localStorage.setItem("adobe_sign_code_verifier", codeVerifier);

    const params = new URLSearchParams({
      response_type: "code",
      client_id: ADOBE_SIGN_CONFIG.clientId,
      redirect_uri: ADOBE_SIGN_CONFIG.redirectUri,
      scope: ADOBE_SIGN_CONFIG.scope,
      state: this.generateState(),
      code_challenge: codeChallenge,
      code_challenge_method: "S256",
    });

    return `${ADOBE_SIGN_CONFIG.authUrl}?${params.toString()}`;
  }

  // Generate random state parameter for security
  generateState() {
    return (
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15)
    );
  }

  // Generate PKCE code verifier
  generateCodeVerifier() {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode.apply(null, array))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=/g, "");
  }

  // Generate PKCE code challenge
  async generateCodeChallenge(codeVerifier) {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await crypto.subtle.digest("SHA-256", data);
    return btoa(String.fromCharCode.apply(null, new Uint8Array(digest)))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=/g, "");
  }

  // Exchange authorization code for tokens
  async exchangeCodeForTokens(code) {
    // Load code verifier from localStorage if not in memory
    if (!this.codeVerifier) {
      this.codeVerifier = localStorage.getItem("adobe_sign_code_verifier");
    }

    if (!this.codeVerifier) {
      throw new Error(
        "Code verifier not found. Please restart the OAuth flow."
      );
    }

    const response = await fetch(ADOBE_SIGN_CONFIG.tokenUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: new URLSearchParams({
        grant_type: "authorization_code",
        code: code,
        client_id: ADOBE_SIGN_CONFIG.clientId,
        client_secret: ADOBE_SIGN_CONFIG.clientSecret,
        redirect_uri: ADOBE_SIGN_CONFIG.redirectUri,
        code_verifier: this.codeVerifier, // PKCE parameter
      }),
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Token exchange failed: ${error}`);
    }

    const data = await response.json();
    this.saveTokens(data.access_token, data.refresh_token, data.expires_in);

    // Clean up code verifier after successful token exchange
    this.codeVerifier = null;
    localStorage.removeItem("adobe_sign_code_verifier");

    return data;
  }
}

// Create singleton instance
export const adobeSignAuth = new AdobeSignAuth();

// Adobe Sign OAuth 2.0 Authentication Functions
export const initiateAdobeSignAuth = async () => {
  if (!ADOBE_SIGN_CONFIG.clientId) {
    throw new Error(
      "Adobe Sign Client ID not configured. Please set REACT_APP_ADOBE_SIGN_CLIENT_ID environment variable."
    );
  }

  try {
    const authUrl = await adobeSignAuth.getAuthorizationUrl();
    window.location.href = authUrl;
  } catch (error) {
    console.error("Error generating authorization URL:", error);
    throw new Error("Failed to initiate Adobe Sign authentication");
  }
};

export const handleAdobeSignCallback = async (code, state) => {
  try {
    const tokens = await adobeSignAuth.exchangeCodeForTokens(code);
    return { success: true, tokens };
  } catch (error) {
    console.error("Adobe Sign callback error:", error);
    return { success: false, error: error.message };
  }
};

export const isAdobeSignAuthenticated = () => {
  return adobeSignAuth.isTokenValid();
};

export const logoutAdobeSign = () => {
  adobeSignAuth.clearTokens();
};

// Adobe Sign API Client
class AdobeSignAPI {
  async makeRequest(endpoint, options = {}) {
    try {
      const accessToken = await adobeSignAuth.getValidAccessToken();

      const url = `${ADOBE_SIGN_CONFIG.baseUrl}${endpoint}`;
      const defaultOptions = {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      };

      const response = await fetch(url, {
        ...defaultOptions,
        ...options,
        headers: {
          ...defaultOptions.headers,
          ...options.headers,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Adobe Sign API error: ${response.status} - ${errorText}`
        );
      }

      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        return await response.json();
      }

      return await response.text();
    } catch (error) {
      console.error("Adobe Sign API request failed:", error);
      throw error;
    }
  }

  // Get user information
  async getUserInfo() {
    return this.makeRequest("/users/me");
  }

  // Create an agreement
  async createAgreement(agreementData) {
    return this.makeRequest("/agreements", {
      method: "POST",
      body: JSON.stringify(agreementData),
    });
  }

  // Get agreement details
  async getAgreement(agreementId) {
    return this.makeRequest(`/agreements/${agreementId}`);
  }

  // Get agreement status
  async getAgreementStatus(agreementId) {
    return this.makeRequest(`/agreements/${agreementId}/status`);
  }

  // Get agreement documents
  async getAgreementDocuments(agreementId) {
    return this.makeRequest(`/agreements/${agreementId}/documents`);
  }

  // Download agreement document
  async downloadAgreementDocument(agreementId, documentId) {
    return this.makeRequest(
      `/agreements/${agreementId}/documents/${documentId}`
    );
  }

  // Cancel an agreement
  async cancelAgreement(agreementId, comment) {
    return this.makeRequest(`/agreements/${agreementId}/state`, {
      method: "PUT",
      body: JSON.stringify({
        state: "CANCELLED",
        comment: comment || "Agreement cancelled",
      }),
    });
  }

  // Get user's agreements
  async getUserAgreements(params = {}) {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        queryParams.append(key, value);
      }
    });

    const queryString = queryParams.toString();
    const endpoint = queryString ? `/agreements?${queryString}` : "/agreements";
    return this.makeRequest(endpoint);
  }

  // Create a library document
  async createLibraryDocument(documentData) {
    return this.makeRequest("/libraryDocuments", {
      method: "POST",
      body: JSON.stringify(documentData),
    });
  }

  // Get library documents
  async getLibraryDocuments() {
    return this.makeRequest("/libraryDocuments");
  }

  // Get webhooks
  async getWebhooks() {
    return this.makeRequest("/webhooks");
  }

  // Create webhook
  async createWebhook(webhookData) {
    return this.makeRequest("/webhooks", {
      method: "POST",
      body: JSON.stringify(webhookData),
    });
  }

  // Template operations
  async getTemplates() {
    return this.makeRequest("/libraryDocuments");
  }

  async getTemplate(templateId) {
    return this.makeRequest(`/libraryDocuments/${templateId}`);
  }

  async createAgreementFromTemplate(templateId, agreementData) {
    return this.makeRequest(`/libraryDocuments/${templateId}/agreements`, {
      method: "POST",
      body: JSON.stringify(agreementData),
    });
  }

  // Agreement management
  async getAgreementFormFields(agreementId) {
    return this.makeRequest(`/agreements/${agreementId}/formFields`);
  }

  async updateAgreementFormFields(agreementId, formFields) {
    return this.makeRequest(`/agreements/${agreementId}/formFields`, {
      method: "PUT",
      body: JSON.stringify(formFields),
    });
  }

  // Document operations
  async getAgreementAuditTrail(agreementId) {
    return this.makeRequest(`/agreements/${agreementId}/auditTrail`);
  }

  async getAgreementSigningUrls(agreementId) {
    return this.makeRequest(`/agreements/${agreementId}/signingUrls`);
  }
}

// Create singleton instance
export const adobeSignAPI = new AdobeSignAPI();

// Function for making authenticated API requests
export const fetchAPI = async (
  endpoint,
  method = "GET",
  queryParams = {},
  bodyData = null,
  onSuccess = null,
  onError = null
) => {
  try {
    const token = await getToken();
    if (!token) {
      throw new Error("Authentication token not available");
    }

    // Build URL with query parameters
    const url = new URL(`${API_BASE_URL}/${endpoint}`);
    Object.entries(queryParams).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((item) => url.searchParams.append(key, item));
      } else if (value !== null && value !== undefined) {
        url.searchParams.append(key, value);
      }
    });

    const options = {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };

    if (typeof FormData !== "undefined" && bodyData instanceof FormData) {
      options.body = bodyData;
    } else if (typeof bodyData === "string") {
      options.body = bodyData;
      options.headers["Content-Type"] = "text/plain";
    } else if (
      bodyData &&
      (method === "POST" || method === "PUT" || method === "PATCH")
    ) {
      options.body = JSON.stringify(bodyData);
      options.headers["Content-Type"] = "application/json";
    }

    const response = await fetch(url.toString(), options);

    const contentType = response.headers.get("content-type");

    const responseData =
      contentType && contentType.includes("application/json")
        ? await response.json()
        : await response.text();

    if (response.ok && onSuccess) {
      onSuccess();
    }

    if (!response.ok && onError) {
      onError();
    }

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        const error = new Error("Authentication failed");
        error.response = response;
        error.data = responseData;
        throw error;
      }

      // For user data endpoint, return null on 404 to indicate user doesn't exist
      if (response.status === 404 && endpoint.startsWith("users/")) {
        return null;
      }

      let errorData = responseData;
      if (typeof responseData === 'string') {
        try { errorData = JSON.parse(responseData); } catch { /* keep plain text */ }
      }
      const error = new Error(
        (typeof errorData === 'object' && errorData?.message) ||
        (typeof errorData === 'string' && errorData) ||
        `Failed to ${method.toLowerCase()} ${endpoint}`
      );
      error.response = response;
      error.data = errorData;
      throw error;
    }

    return responseData;
  } catch (error) {
    console.error(`Error in ${endpoint}:`, error);
    throw error;
  }
};
